/*
 * <scentt-carousel> \u2013 progressive enhancement for a horizontal scroller (.scentt-rail).
 * Markup:
 *   <scentt-carousel data-autoplay="0" data-autoplay-desktop="false" data-hints="fade progress nudge">
 *     <ul class="scentt-rail" id="Rail-{{ section.id }}">\u2026</ul>
 *     <div class="scentt-rail__progress" hidden><span></span></div>   (optional)
 *   </scentt-carousel>
 *   Arrows anywhere on the page: <button data-scentt-prev="Rail-\u2026"> / <button data-scentt-next="Rail-\u2026">
 * Works without JavaScript (native scrolling); JS only adds arrows state, hints and autoplay.
 */
(() => {
  if (customElements.get('scentt-carousel')) return;
  const S = window.Scentt && Object.isFrozen(window.Scentt) && typeof window.Scentt.html === 'function' ? window.Scentt : null;
  const motion = () => (S && S.reducedMotion ? 'auto' : 'smooth');

  class ScenttCarousel extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('.scentt-rail');
      if (!this.rail || !this.rail.id) return;
      this.hints = (this.dataset.hints || '').split(/\s+/);
      this.bar = this.querySelector('.scentt-rail__progress');
      this.onScroll = () => { if (!this.ticking) { this.ticking = true; requestAnimationFrame(() => { this.ticking = false; this.update(); }); } };
      this.rail.addEventListener('scroll', this.onScroll, { passive: true });
      this.resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(this.onScroll) : null;
      if (this.resizeObserver) this.resizeObserver.observe(this.rail);
      this.update();
      const stopView = S ? S.inView(this, () => this.start(), '0px') : this.start();
      this.cleanup = typeof stopView === 'function' ? stopView : () => {};
    }

    disconnectedCallback() {
      if (this.rail) this.rail.removeEventListener('scroll', this.onScroll);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      clearInterval(this.timer);
      if (this.cleanup) this.cleanup();
    }

    start() {
      if (this.hints.includes('nudge')) this.nudge();
      const secs = S ? S.int(this.dataset.autoplay, 0, 0, 30) : 0;
      if (!secs || (S && S.reducedMotion)) return;
      let pausedUntil = 0;
      const hold = () => { pausedUntil = Date.now() + 4000; };
      ['pointerdown', 'touchstart', 'focusin', 'mouseenter'].forEach((ev) => this.rail.addEventListener(ev, hold, { passive: true }));
      this.timer = setInterval(() => {
        if (Date.now() < pausedUntil || document.hidden || !this.isConnected) return;
        if (this.dataset.autoplayDesktop !== 'true' && window.matchMedia('(min-width: 750px)').matches) return;
        const item = this.rail.firstElementChild;
        const step = item ? item.getBoundingClientRect().width : this.rail.clientWidth;
        const atEnd = this.rail.scrollLeft >= this.rail.scrollWidth - this.rail.clientWidth - 2;
        this.rail.scrollTo({ left: atEnd ? 0 : this.rail.scrollLeft + step, behavior: motion() });
      }, secs * 1000);
    }

    update() {
      const r = this.rail;
      const max = r.scrollWidth - r.clientWidth;
      const scrollable = max > 4;
      this.classList.toggle('is-scrollable', scrollable);
      if (this.hints.includes('fade')) {
        this.classList.toggle('has-left', scrollable && r.scrollLeft > 4);
        this.classList.toggle('has-right', scrollable && r.scrollLeft < max - 4);
      }
      document.querySelectorAll(`[data-scentt-prev="${CSS.escape(r.id)}"]`).forEach((b) => { b.disabled = !scrollable || r.scrollLeft <= 2; });
      document.querySelectorAll(`[data-scentt-next="${CSS.escape(r.id)}"]`).forEach((b) => { b.disabled = !scrollable || r.scrollLeft >= max - 2; });
      if (this.bar && this.hints.includes('progress')) {
        this.bar.hidden = !scrollable;
        if (scrollable) {
          const w = this.bar.clientWidth;
          const thumb = Math.max(14, (w * r.clientWidth) / r.scrollWidth);
          this.bar.style.setProperty('--thumb', `${thumb}px`);
          this.bar.style.setProperty('--pos', `${(w - thumb) * (r.scrollLeft / max)}px`);
        }
      }
    }

    nudge() {
      const r = this.rail;
      if (S && S.reducedMotion) return;
      if (r.scrollWidth - r.clientWidth < 5 || r.scrollLeft > 4) return;
      /* Phones only: on desktop the arrows are the hint, and a nudge could undo an early arrow click. */
      if (window.matchMedia('(min-width: 750px)').matches) return;
      let touched = false;
      const stop = () => { touched = true; };
      ['touchstart', 'pointerdown', 'wheel', 'keydown'].forEach((ev) => r.addEventListener(ev, stop, { once: true, passive: true }));
      setTimeout(() => {
        if (touched) return;
        r.scrollTo({ left: 56, behavior: 'smooth' });
        setTimeout(() => { if (!touched) r.scrollTo({ left: 0, behavior: 'smooth' }); }, 650);
      }, 700);
    }

    step(dir) {
      const r = this.rail;
      r.scrollBy({ left: dir * Math.max(r.clientWidth * 0.8, 200), behavior: motion() });
    }
  }
  customElements.define('scentt-carousel', ScenttCarousel);

  /* One delegated listener for every arrow on the page. */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('[data-scentt-prev], [data-scentt-next]');
    if (!btn) return;
    const id = btn.getAttribute('data-scentt-prev') || btn.getAttribute('data-scentt-next');
    const rail = id && document.getElementById(id);
    const host = rail && rail.closest('scentt-carousel');
    if (host && typeof host.step === 'function') host.step(btn.hasAttribute('data-scentt-next') ? 1 : -1);
  });
})();
